import React, { useEffect, useState } from "react";
import { Loader2, Check, Crown, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PERKS = [
  "Full access to every recovery Program",
  "Unlimited Ask AI guidance",
  "Advanced gamification & insights",
  "Unlimited direct messages",
  "Support the mission — Greater Things",
];

const Upgrade = () => {
  const { isPremium } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/billing/plans");
        setPlans(data.plans || []);
      } catch (e) { toast.error("Could not load plans"); }
      finally { setLoading(false); }
    })();
  }, []);

  const checkout = async (planId) => {
    setBusy(planId);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: planId,
        origin_url: window.location.origin,
      });
      const url = data.url || data.checkout_url;
      if (url) window.location.href = url;
      else toast.error("Checkout unavailable right now");
    } catch (e) {
      toast.error(errMsg(e, "Could not start checkout"));
    } finally { setBusy(""); }
  };

  return (
    <div className="px-4 sm:px-6 py-8">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <div className="w-14 h-14 rounded-full bg-brass/20 text-brass flex items-center justify-center mx-auto mb-4">
            <Crown className="w-7 h-7" />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold">Compass Premium</h1>
          <p className="text-muted-foreground mt-2">Unlock the full journey. Cancel anytime.</p>
          {isPremium && (
            <div className="inline-flex items-center gap-2 mt-4 px-4 py-1.5 rounded-full bg-seafoam/15 text-seafoam text-sm font-medium">
              <Sparkles className="w-4 h-4" /> You're already Premium
            </div>
          )}
        </div>

        <div className="bg-card border border-border rounded-2xl p-6 mb-8">
          <ul className="grid sm:grid-cols-2 gap-3">
            {PERKS.map((p) => (
              <li key={p} className="flex items-start gap-2 text-foreground/90">
                <Check className="w-5 h-5 text-seafoam shrink-0 mt-0.5" /> {p}
              </li>
            ))}
          </ul>
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-5" data-testid="plans-grid">
            {plans.map((p) => (
              <div key={p.id} data-testid={`plan-${p.id}`} className="bg-card border border-border rounded-2xl p-6 hover:border-primary transition-colors">
                <p className="text-sm text-muted-foreground">{p.label}</p>
                <p className="font-heading text-3xl font-semibold mt-1">
                  ${p.amount}
                  <span className="text-base text-muted-foreground font-body"> / {p.days >= 365 ? "year" : "month"}</span>
                </p>
                <button data-testid={`checkout-${p.id}`} onClick={() => checkout(p.id)} disabled={!!busy}
                  className="mt-5 w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground font-semibold py-3 rounded-full hover:bg-atlantic active:scale-95 transition-all disabled:opacity-60">
                  {busy === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
                  Choose {p.label}
                </button>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-muted-foreground text-center mt-6">Secure checkout powered by Stripe.</p>
      </div>
    </div>
  );
};

export default Upgrade;
